import BrickBox from '@/components/brick-box';
import { connectColors } from '@/theme/colors';
import { Button, Modal } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import type { RefObject } from 'react';
import { CreatePriceMode } from '../create-price-search-params';

type SaveConfirmationModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onConfirm: () => void;
  onPublishAndGoToApprove?: () => void;
  isSaving: boolean;
  totalUpdatedCount: number;
  approvalCount: number;
  totalPreliminaryCount: number;
  mode: CreatePriceMode;
};

export const SaveConfirmationModal = ({
  isOpen,
  triggerRef,
  onClose,
  onConfirm,
  onPublishAndGoToApprove,
  isSaving,
  totalUpdatedCount,
  approvalCount,
  totalPreliminaryCount,
  mode,
}: SaveConfirmationModalProps) => {
  const isApproveMode = mode === CreatePriceMode.Approve;
  const hasApprovals = approvalCount > 0;

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const modalTitle = isApproveMode
    ? 'Confirm approval'
    : 'Confirm pricing changes';

  const confirmButtonText = isApproveMode ? 'Approve' : 'Publish';
  const showPublishAndGoToApprove =
    !isApproveMode && totalPreliminaryCount > 0 && onPublishAndGoToApprove;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      triggerRef={triggerRef}
      size="medium"
    >
      <Modal.Title>{modalTitle}</Modal.Title>
      <Modal.Content>
        <Flex direction="column" gap="16px">
          <Text color={connectColors.core.offBlack}>
            These updates will apply immediately.
          </Text>

          <BrickBox color={connectColors.core.green[50]}>
            <Flex direction="column" gap="8px" padding="16px">
              {!isApproveMode && (
                <Text color={connectColors.core.offBlack}>
                  <Text as="span" fontWeight="bold">
                    {totalUpdatedCount}{' '}
                    {totalUpdatedCount === 1 ? 'product' : 'products'}
                  </Text>{' '}
                  updated
                </Text>
              )}
              {hasApprovals && (
                <Text color={connectColors.core.offBlack}>
                  <Text as="span" fontWeight="bold">
                    {approvalCount}{' '}
                    {approvalCount === 1 ? 'product' : 'products'}
                  </Text>
                  : <Text as="span">Preliminary → Approved</Text>
                </Text>
              )}
            </Flex>
          </BrickBox>
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Button
          appearance="ghost"
          variant="primary"
          size="medium"
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        {showPublishAndGoToApprove && (
          <Button
            appearance="outline"
            size="medium"
            onClick={onPublishAndGoToApprove}
            disabled={isSaving}
            loading={isSaving}
          >
            Publish and go to Approve
          </Button>
        )}
        <Button
          appearance="solid"
          variant="primary"
          size="medium"
          onClick={onConfirm}
          disabled={isSaving}
          loading={isSaving}
        >
          {confirmButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
